import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { fetchBlogPost } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    fetchBlogPost(slug).then(setPost).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="blog-not-found" className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Article not found</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-[#0052FF]">
          <ArrowLeft className="w-4 h-4" /> Back to all posts
        </Link>
      </div>
    );
  }

  if (!post) return <div className="mx-auto max-w-3xl px-6 py-24 text-slate-500">Loading…</div>;

  return (
    <article data-testid="blog-post" className="bg-white">
      <div className="mx-auto max-w-3xl px-6 lg:px-10 pt-14 md:pt-20">
        <Link to="/blog" className="text-sm inline-flex items-center gap-1 text-slate-600 hover:text-slate-950">
          <ArrowLeft className="w-4 h-4" /> All articles
        </Link>
        <div className="mt-8 flex flex-wrap gap-1">
          {post.tags.map((t) => (
            <span key={t} className="text-[11px] px-2 py-0.5 border border-slate-200 text-slate-700 bg-slate-50">{t}</span>
          ))}
        </div>
        <h1 className="mt-6 font-display font-bold tracking-tighter text-4xl md:text-5xl leading-[1.05] text-slate-950">
          {post.title}
        </h1>
        <div className="mt-6 flex items-center gap-4 text-sm text-slate-500 font-mono-plex uppercase tracking-widerest">
          <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />{post.author}</span>
          <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{fmt(post.published_at)}</span>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 lg:px-10 mt-10">
        <img src={post.cover_image} alt={post.title} className="w-full h-[300px] md:h-[440px] object-cover border border-slate-200" />
      </div>

      <div className="mx-auto max-w-3xl px-6 lg:px-10 py-12 md:py-16">
        {post.content.split("\n\n").map((para, i) => (
          <p key={i} className="text-slate-800 text-lg leading-relaxed mb-6 font-serif" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {para}
          </p>
        ))}
        <div className="mt-12 border-t border-slate-200 pt-8">
          <div className="text-xs uppercase tracking-widerest font-mono-plex text-slate-500">Take the next step</div>
          <h3 className="mt-2 font-display text-2xl font-bold text-slate-950">Ready to try a live class?</h3>
          <Link
            to="/pay-demo"
            className="mt-4 inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium px-6 py-3 transition-colors"
            data-testid="blog-post-cta-demo"
          >
            Book a free demo
          </Link>
        </div>
      </div>
    </article>
  );
}
